/**
 * Thinccc Design System - Floating Action Button (FAB)
 *
 * From Figma:
 * - Sizes: small (36px), medium (56px), large (96px), extended
 * - Styles: surface, primary, secondary, tertiary
 */
import React from "react";

import PropTypes from "prop-types";

// ===========================================
// VARIANT STYLES
// ===========================================
const variantStyles = {
  surface: `
    bg-background-cloudburst text-text-lv1
    hover:bg-background-biscay hover:shadow-elevation-4
    active:bg-background-astronaut
    focus-visible:focus-ring
    shadow-elevation-3
  `,
  primary: `
    bg-persimmon text-white
    hover:bg-red-400 hover:shadow-elevation-4
    active:bg-red-500
    focus-visible:focus-ring
    shadow-elevation-3
  `,
  secondary: `
    bg-charlotte text-background-blackpearl
    hover:bg-blue-300 hover:shadow-elevation-4
    active:bg-blue-400
    focus-visible:focus-ring
    shadow-elevation-3
  `,
  tertiary: `
    bg-background-biscay text-charlotte
    hover:bg-background-astronaut hover:shadow-elevation-4
    active:bg-background-cloudburst
    focus-visible:focus-ring
    shadow-elevation-3
  `,
};

// ===========================================
// SIZE STYLES
// ===========================================
const sizeStyles = {
  small: "w-9 h-9 rounded-lg",
  medium: "w-14 h-14 rounded-2xl",
  large: "w-24 h-24 rounded-3xl",
};

const iconSizes = {
  small: "w-5 h-5",
  medium: "w-6 h-6",
  large: "w-9 h-9",
};

// ===========================================
// FAB COMPONENT
// ===========================================
const FAB = React.forwardRef(
  (
    {
      icon,
      variant = "primary",
      size = "medium",
      label,
      extended = false,
      disabled = false,
      className = "",
      onClick,
      ...props
    },
    ref,
  ) => {
    // Extended FAB has label + icon
    if (extended) {
      return (
        <button
          ref={ref}
          type="button"
          className={`
          inline-flex items-center justify-center gap-3
          h-14 px-6
          rounded-2xl
          font-medium text-base tracking-button
          transition-all duration-normal ease-standard
          select-none
          ${variantStyles[variant]}
          ${disabled ? "opacity-38 cursor-not-allowed shadow-none" : ""}
          ${className}
        `
            .replace(/\s+/g, " ")
            .trim()}
          disabled={disabled}
          onClick={onClick}
          {...props}
        >
          <span className="w-6 h-6 flex-shrink-0">{icon}</span>
          <span>{label}</span>
        </button>
      );
    }

    // Regular FAB (icon only)
    return (
      <button
        ref={ref}
        type="button"
        className={`
        inline-flex items-center justify-center
        transition-all duration-normal ease-standard
        select-none
        ${sizeStyles[size]}
        ${variantStyles[variant]}
        ${disabled ? "opacity-38 cursor-not-allowed shadow-none" : ""}
        ${className}
      `
          .replace(/\s+/g, " ")
          .trim()}
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        {...props}
      >
        <span className={iconSizes[size]}>{icon}</span>
      </button>
    );
  },
);

FAB.displayName = "FAB";

// ===========================================
// PROP TYPES
// ===========================================
FAB.propTypes = {
  icon: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(["surface", "primary", "secondary", "tertiary"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  label: PropTypes.string,
  extended: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
};

export default FAB;
